export interface Project {
  id: string;
  title: string;
  description: string;
  link: string;
  githubLink?: string;
  metrics: {
    label: string;
    value: string;
  }[];
  technologies: string[];
  imageUrl?: string;
}

export const projects: Project[] = [
  {
    id: '1',
    title: 'AI-for-Sarcopenia Detection',
    description: 'Deep learning pipeline for automated L3 vertebrae segmentation and sarcopenia detection from clinical CT scans. Reduces radiologist diagnosis time from 30 minutes to 1.5 seconds per patient.',
    link: 'https://github.com/SenpaiSepehr/AI-for-Sarcopenia',
    metrics: [
      { label: 'Dice Similarity Coefficient', value: '0.913 ± 0.03' },
      { label: 'Diagnosis Time Reduction', value: '30min to 1.5s' },
      { label: 'Localization Accuracy', value: '2.11mm (frontal) -3.44mm (sagittal)' }
    ],
    technologies: ['Python', 'Medical Imaging', 'Computer Vision', 'Deep Learning', 'Transformers'],
    imageUrl: '/images/project-icons/medical-scan.svg'
  },
  {
    id: '2',
    title: 'Endometriosis Detection AI',
    description: 'AI-powered early detection pipeline implementing SonoSAM segmentation model for ultrasound tracking. Automated analysis of 120+ patient datasets from BC women\'s health records. Research co-authored into a paper accepted at BMVC 2026.',
    link: 'https://github.com/JaiJC/endometriosis-ai',
    metrics: [
      { label: 'Detection Time Reduction', value: '40%' },
      { label: 'Dice Similarity Coefficient', value: '81.83%' },
      { label: 'Published Paper', value: 'BMVC 2026' }
    ],
    technologies: ['Python', 'SonoSAM', 'Machine Learning', 'Ultrasound Analysis'],
    imageUrl: '/images/project-icons/endometriosis.svg'
  },
  {
    id: '3',
    title: 'Genome Informatics Pipeline',
    description: 'Fully automated Snakemake pipeline integrating BWA-MEM alignment, quality control, and DESeq2 differential expression analysis. Extended findings from Kavvas et al. with significant throughput improvements.',
    link: 'https://github.com/JaiJC/Genome_Informatics_Project',
    metrics: [
      { label: 'Processing Throughput', value: '275% Increase' },
      { label: 'Alignment Rates', value: '>98%' },
      { label: 'Gene Detection Improvement', value: '35%' }
    ],
    technologies: ['Python', 'Snakemake', 'Bash', 'RNA-seq', 'Bioinformatics', 'DESeq2'],
    imageUrl: '/images/project-icons/genome.svg'
  },
  {
    id: '4',
    title: 'Fall Detection System for Wearables',
    description: 'Neural network-based fall detection system using IMU sensors, PPG, ECG, and FFT signal processing. Outperformed commercial devices including Apple Watch Series 9 and Homesafe Lifeline in graduate-level competition.',
    link: 'https://github.com/JaiJC/Fall-Near-Fall_Detection',
    metrics: [
      { label: 'F1 Score', value: '87%' },
      { label: 'Graduate Grand Challenge', value: 'Ranked #1' },
      { label: 'Multi-sensor Fusion', value: 'Implemented' }
    ],
    technologies: ['MATLAB', 'Neural Networks', 'Signal Processing', 'IMU Sensors', 'Sensor Fusion'],
    imageUrl: '/images/project-icons/wearable.svg'
  },
  {
    id: '5',
    title: 'CAR-Macrophage Immunotherapy Research',
    description: 'Theoretical research designing CAR constructs with anti-CD47 and anti-HER2 domains to enhance macrophage-mediated tumor phagocytosis. Developed RNA-LNP formulation for targeted delivery. Published in Canadian Undergraduate Research Journal 2025. Oral presentation at MURC 2025, UBC.',
    link: 'https://cjur.ca/wp-content/uploads/2025/06/2025-06-08-MURCxCJUR-v2.pdf',
    metrics: [
      { label: 'CAR Constructs', value: 'Multiple Designs' },
      { label: 'Theoretical Framework', value: 'Published' },
      { label: 'Oral Presentation', value: 'MURC 2025' }
    ],
    technologies: ['Immunotherapy', 'CAR Design', 'RNA-LNP', 'Cancer Research', 'Theoretical Models'],
    imageUrl: '/images/project-icons/immunotherapy.svg'
  },
  {
    id: '7',
    title: 'TP53 in Liver Cancer: Mutation Analysis',
    description: 'Comprehensive analysis of 23 highly mutated genes in HCC patients through hierarchical clustering, expression profiling, and survival analysis. Identified TP53 mutation as significantly associated with reduced patient survival via Kaplan-Meier survival analysis.',
    link: '/projects/tps3-liver-cancer',
    metrics: [
      { label: 'Genes Analyzed', value: '23' },
      { label: 'Key Finding', value: 'TP53 Survival Link' },
      { label: 'Analysis Methods', value: 'Multiple' }
    ],
    technologies: ['R', 'Bioinformatics', 'Gene Expression', 'Survival Analysis', 'Cancer Genomics'],
    imageUrl: '/images/project-icons/liver-cancer.svg'
  },
  {
    id: '8',
    title: 'Chem-E-Car Competition',
    description: 'Designed and built miniature autonomous vehicle for AICHE Chem-E-Car competition. Engineered car to travel randomly assigned distances using only chemical reactions as stopping mechanism.',
    link: 'https://chbe.ubc.ca/news/2024/ubc-chem-e-car-successful-at-aiche-pnw-2/',
    metrics: [
      { label: 'Competition Result', value: 'Won AIChE Regionals (Oregon)' },
      { label: 'AIChE North America', value: '17th Place' },
      { label: 'System Type', value: 'Chemical Reaction' }
    ],
    technologies: ['Chemical Engineering', 'Reaction Kinetics', '3D Printing', 'Control Systems', 'Prototyping'],
    imageUrl: '/images/project-icons/chem-e-car.svg'
  },
  {
    id: '10',
    title: 'GRASP: Guided Robotic Assistant for Surgical Procedures',
    description: 'Advanced surgical robotics system designed to address nursing shortages in perioperative care by autonomously managing surgical tool inventory and safely passing instruments to surgeons. Features a 7-link manipulator with spherical wrist, RGB-D camera monitoring, 4x LIDAR scanners, and dynamic 3D virtual fixtures for collision avoidance. Equipped with speech recognition, deep learning for accent detection, and force/tactile sensors for precise tool handling.',
    link: '/assets/GRASP.pdf',
    metrics: [
      { label: 'Workspace Radius', value: '1.46m' },
      { label: 'Load Capacity', value: '25 lbs' },
      { label: 'End Effector Resolution', value: 'Sub-millimeter' },
      { label: 'Joint Resolution', value: '0.01° - 0.1°' }
    ],
    technologies: ['Robotics', 'Kinematics', 'LIDAR', 'Computer Vision', 'Deep Learning', 'Speech Recognition', 'CAD', 'Virtual Fixtures', 'Sensor Fusion'],
    imageUrl: '/images/project-icons/robot.svg'
  },
  {
    id: '11',
    title: 'HiddenCity: AI Local Discovery',
    description: 'HiddenCity is an AI-powered local discovery engine that surfaces "offline" micro-businesses that never show up on Google using street-level imagery and social signals instead of traditional SEO. It analyzes storefront photos with vision-language models, classifies and enriches them, and exposes a map-based search UI so consumers see all nearby options, not just those with claimed listings. Led a 3-person team.',
    link: '#',
    metrics: [
      { label: 'Target Strategy', value: '56% unclaimed listings' },
      { label: 'Demo Dataset', value: 'Vancouver storefronts' },
    ],
    technologies: ['React', 'Tailwind', 'TypeScript', 'FastAPI', 'Python', 'SQLite', 'GeoJSON', 'VLM', 'OCR', 'Google Maps API', 'AI'],
    imageUrl: '/images/project-icons/map.svg'
  },
  {
    id: '12',
    title: 'ResuME: Optimizing Networking',
    description: 'iOS app optimizing in-person networking at career fairs, conferences, and trade fairs. The Follow-up rate, due to various reasons, is incredibly low. In this fast paced world, where our focus on information has been limited, ResuME acts as a bridge making in person connections useful the way they were intendeded to. Also has features like AI role matching that scores candidate profiles against open positions, dynamic/tailored resume generation, and AI-powered follow-up drafting from post-meeting conversation logs, and business card readers with follow ups drafted just waiting for you to press send. Shipped a live MVP serving job seekers, recruiters, and business professionals at pilot events.',
    link: 'https://get-resume.app',
    metrics: [
      { label: 'Deployment Status', value: 'Live MVP' },
      { label: 'Core Feature', value: 'AI Role Matching' },
      { label: 'Live Product', value: 'get-resume.app' }
    ],
    technologies: ['Swift', 'iOS', 'Supabase', 'Python', 'AI', 'Machine Learning', 'PostgreSQL', 'Full-Stack']
  },
  {
    id: '13',
    title: 'HPA Axis Modeling & PID Control for PTSD',
    description: 'Closed-loop control system for PTSD episode management. Modeled the hypothalamic-pituitary-adrenal (HPA) axis using nonlinear ODEs and Simulink, calibrating to literature cortisol dynamics in PTSD vs. non-PTSD subjects. Implemented and tuned a PID controller simulating amitriptyline delivery to reduce PTSD-associated peak cortisol (3.10 μg/dL) toward healthy baseline (2.56 μg/dL). Validated against quantitative design requirements for biomarker sensing latency and control performance.',
    link: 'public/assets/HPA PTSD controller.pdf',
    metrics: [
      { label: 'Cortisol Reduction', value: '3.10→2.56 μg/dL' },
      { label: 'Controller Performance', value: '<5% Steady-State Error' },
      { label: 'Model Type', value: 'Nonlinear ODE' }
    ],
    technologies: ['MATLAB', 'Simulink', 'Control Systems', 'ODE Modeling', 'Biomedical Engineering', 'PID Controller']
  },
  {
    id: '14',
    title: 'Infant Sepsis Early Detection Monitor',
    description: 'Wearable monitor for real-time infant vital sign tracking with mobile app alerting for early-onset sepsis detection. Led electronics and hardware design: engineered IP37-rated enclosure in SolidWorks, optimized FDM 3D printing infill patterns for 5-meter drop survivability, and prototyped real-time thermal monitoring with wireless data transmission to a guardian mobile app. Delivered under 60% of allocated budget.',
    link: '#',
    metrics: [
      { label: 'Ingress Protection', value: 'IP37 Rating' },
      { label: 'Durability', value: '5m Drop Survivable' },
      { label: 'Cost Efficiency', value: '60% of Budget' }
    ],
    technologies: ['SolidWorks', 'Embedded Systems', 'Hardware Design', '3D Printing', 'IoT', 'Electronics', 'Prototyping']
  },
  {
    id: '6',
    title: 'Credit Card Default Prediction',
    description: 'LightGBM classification model predicting credit card defaults with high accuracy on 30,000 customer records. Analyzed payment patterns and demographic data to identify financial risk indicators.',
    link: 'https://medium.com/@chorariajai17/will-you-default-83e6f27775ef',
    githubLink: 'https://github.com/JaiJC/Will_You_Default',
    metrics: [
      { label: 'Prediction Accuracy', value: '82%' },
      { label: 'AUC-ROC Score', value: '0.85' },
      { label: 'Accuracy Improvement', value: '35% over Baseline' }
    ],
    technologies: ['Python', 'LightGBM', 'Machine Learning', 'Classification', 'Risk Analysis'],
    imageUrl: '/images/project-icons/credit-card.svg'
  }
];
